import { useState, useEffect } from "react";
import "./Notices.css";


export default function Notices() {

    const [notices, setNotices] = useState([]);
    const [editId, setEditId] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [categoryFilter, setCategoryFilter] = useState("All");
    const [statusFilter, setStatusFilter] = useState("All");
    const [priorityFilter, setPriorityFilter] = useState("All");
    const [selectedNotice, setSelectedNotice] = useState(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);

const [formData, setFormData] = useState({
    title: "",
    description: "",
    category: "",
    priority: "",
    status: "",
    link:"",
    publish_date: "",
});

    const [errors, setErrors] = useState({});

    /* ================= FETCH NOTICES (SAFE) ================= */
    const fetchNotices = async () => {
        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}/notices.php`);
            const data = await res.json();

            if (Array.isArray(data)) {
                setNotices(data);
            } else if (Array.isArray(data.data)) {
                setNotices(data.data);
            } else if (Array.isArray(data.notices)) {
                setNotices(data.notices);
            } else {
                console.error("Unexpected API response:", data);
                setNotices([]);
            }

        } catch (err) {
            console.error("Fetch error:", err);
            setNotices([]);
        }
    };

    useEffect(() => {
        fetchNotices();
    }, []);

    /* ================= HELPERS ================= */
    const normalizeNotice = (n) => ({
        ...n,
        category: n.type ? n.type.charAt(0).toUpperCase() + n.type.slice(1) : "General",
        publishDate: n.date || "",
        priority: n.urgent ? "High" : "Medium",
        status: n.read ? "Archived" : "Active",
        noticeNumber: `NOTICE-${n.id}`
    });

    const validate = () => {
        let err = {};
        if (!formData.title.trim()) err.title = "Title is required";
        if (!formData.description.trim()) err.description = "Description is required";
        return err;
    };

    /* ================= MODAL ================= */
    const openCreateModal = () => {
        setEditId(null);
        setErrors({});
        setFormData({
            title: "",
            description: "",
            category: "General",
            priority: "Medium",
            status: "Active",
            publishDate: new Date().toISOString().split("T")[0]
        });
        setShowCreateModal(true);
    };

    const openEditModal = (notice) => {
        const n = normalizeNotice(notice);
        setEditId(notice.id);
        setErrors({});
        setFormData({
            title: n.title,
            description: n.description,
            category: n.category,
            priority: n.priority,
            status: n.status,
            publishDate: n.publishDate
        });
        setShowCreateModal(true);
    };

    const closeModal = () => {
        setShowCreateModal(false);
        setEditId(null);
    };

    /* ================= CREATE / UPDATE ================= */
    const handleSubmit = async () => {
        const err = validate();
        if (Object.keys(err).length) {
            setErrors(err);
            return;
        }

        const payload = {
            title: formData.title,
            description: formData.description,
            type: formData.category.toLowerCase(),
            notice_date: formData.publish_date,
            link: formData.link,
            urgent: formData.priority === "High"
        };

        try {
            if (editId) {
                await fetch(`${process.env.REACT_APP_API_URL}/notices.php`, {
                    method: "PUT",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        id: editId,
                        ...payload,
                        read: formData.status !== "Active"
                    })
                });
            } else {
                await fetch(`${process.env.REACT_APP_API_URL}/notices.php`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(payload)
                });
            }

            fetchNotices();
            closeModal();

        } catch (err) {
            console.error("Submit error:", err);
        }
    };

    /* ================= DELETE ================= */
   const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this notice?")) return;

    try {
        // Use URLSearchParams to send URL-encoded data
        const formData = new URLSearchParams();
        formData.append("id", id);

        const res = await fetch(`${process.env.REACT_APP_API_URL}/notices.php`, {
            method: "DELETE",
            headers: { "Content-Type": "application/x-www-form-urlencoded" },
            body: formData.toString()
        });

        const data = await res.json();
        if (data.success) {
            alert("Notice deleted successfully!");
            fetchNotices();
        } else {
            alert(data.error || "Failed to delete notice.");
        }
    } catch (err) {
        console.error("Delete error:", err);
        alert("Error connecting to server.");
    }
};


    /* ================= STATUS ================= */
    const handleStatusChange = async (id, status) => {
        try {
            await fetch(`${process.env.REACT_APP_API_URL}/notices.php`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    id,
                    read: status !== "Active"
                })
            });
            fetchNotices();
        } catch (err) {
            console.error("Status error:", err);
        }
    };

    /* ================= FILTER ================= */
    const filteredNotices = Array.isArray(notices)
        ? notices
            .map(normalizeNotice)
            .filter(n =>
                n.title.toLowerCase().includes(searchTerm.toLowerCase()) &&
                (categoryFilter === "All" || n.category === categoryFilter) &&
                (statusFilter === "All" || n.status === statusFilter) &&
                (priorityFilter === "All" || n.priority === priorityFilter)
            )
        : [];

    /* ================= RENDER ================= */
    return (
        <div className="notices-admin">

            <div className="notices-header">
                <h2>📢 Notice Management</h2>
                <button className="btn-create" onClick={openCreateModal}>
                    + Create Notice
                </button>
            </div>

            <input
                className="search-bar"
                type="text"
                placeholder="Search notices..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
            />

            <table className="notices-table">
                <thead>
                    <tr>
                        <th>Title</th>
                        <th>Category</th>
                        <th>Priority</th>
                        <th>Date</th>
                        <th>Status</th>
                        <th>Link</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {filteredNotices.length === 0 ? (
                        <tr>
                            <td colSpan="6" style={{ textAlign: "center" }}>
                                No notices found
                            </td>
                        </tr>
                    ) : (
                        filteredNotices.map(n => (
                            <tr key={n.id}>
                                <td>{n.title}</td>
                                <td>{n.category}</td>
                                <td>{n.priority}</td>
                                <td>{n.publishDate}</td>
                                <td>
                                    <select
                                        value={n.status}
                                        onChange={(e) => handleStatusChange(n.id, e.target.value)}
                                    >
                                        <option value="Active">Active</option>
                                        <option value="Archived">Archived</option>
                                    </select>
                                </td>
                                <td>{n.link}</td>
                                <td>
                                    <button onClick={() => openEditModal(n)}>✏️</button>
                                    <button onClick={() => handleDelete(n.id)}>🗑️</button>
                                </td>
                            </tr>
                        ))
                    )}
                </tbody>
            </table>

          {showCreateModal && (
    <div className="modal-overlay">
        <div className="modal-container">
            <h3>{editId ? "Edit Notice" : "Create Notice"}</h3>

            {/* Title */}
            <input
                placeholder="Title"
                value={formData.title}
                onChange={(e) =>
                    setFormData({ ...formData, title: e.target.value })
                }
            />
            {errors.title && <p className="error-text">{errors.title}</p>}
{/* Status */}
            <select
                value={formData.status}
                onChange={(e) =>
                    setFormData({ ...formData, status: e.target.value })
                }
            >
                <option value="">Select Status</option>
                <option value="Active">Active</option>
                <option value="Archived">Archived</option>
            </select>
            {errors.status && (
                <p className="error-text">{errors.status}</p>
            )}
            {/* Description */}
            <textarea
                placeholder="Description"
                value={formData.description}
                onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                }
            />
            {errors.description && (
                <p className="error-text">{errors.description}</p>
            )}

            {/* Category */}
            <select
                value={formData.category}
                onChange={(e) =>
                    setFormData({ ...formData, category: e.target.value })
                }
            >
                <option value="">Select Category</option>
                <option value="General">General</option>
                <option value="Event">Event</option>
                <option value="Meeting">Meeting</option>
                <option value="Urgent">Urgent</option>
            </select>
            {errors.category && (
                <p className="error-text">{errors.category}</p>
            )}

            {/* Priority */}
            <select
                value={formData.priority}
                onChange={(e) =>
                    setFormData({ ...formData, priority: e.target.value })
                }
            >
                <option value="">Select Priority</option>
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
            </select>
            {errors.priority && (
                <p className="error-text">{errors.priority}</p>
            )}

            

            {/* Publish Date */}
            <input
                type="date"
                value={formData.publish_date}
                onChange={(e) =>
                    setFormData({
                        ...formData,
                        publish_date: e.target.value,
                    })
                }
            />

            {errors.publish_date && (
                <p className="error-text">{errors.publish_date}</p>
            )}

            {/* Link */}
            <input
                type="text"
                placeholder="Link"
                value={formData.link}
                onChange={(e) =>
                    setFormData({ ...formData, link: e.target.value })
                }
            />
            {errors.link && (
                <p className="error-text">{errors.link}</p>
            )}

            {/* Buttons */}
            <div className="modal-actions">
                <button onClick={handleSubmit}>
                    {editId ? "Update Notice" : "Create Notice"}
                </button>
                <button onClick={closeModal}>Cancel</button>
            </div>
        </div>
    </div>
)}


        </div>
    );
}
